import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sun, ArrowRight, Calendar, Clock, Loader2 } from "lucide-react";
import { Seo, siteUrl } from "@/components/Seo";
import FeedbackLink from "@/components/FeedbackLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blog/posts`).then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "HomeSchool Hub Blog",
    url: `${siteUrl}/blog`,
    description:
      "Guides, compliance walkthroughs, and scholarship deep-dives for Florida homeschool teachers.",
    blogPost: (articles || []).map((a) => ({
      "@type": "BlogPosting",
      headline: a.title,
      description: a.description,
      datePublished: a.date,
      url: `${siteUrl}/blog/${a.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-stone-900" data-testid="blog-page">
      <Seo
        title="Florida Homeschool Blog — Compliance, Scholarships, Assessments"
        description="Long-form guides for Florida homeschool teachers: FES-EO scholarships, WRAT-5 assessment prep, portfolio compliance, and more."
        path="/blog"
        jsonLd={jsonLd}
      />

      <header className="sticky top-0 z-40 glass border-b border-stone-200/60">
        <div className="mx-auto max-w-5xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg" data-testid="blog-brand-link">
            <span className="inline-grid place-items-center w-9 h-9 rounded-2xl bg-amber-500 text-white">
              <Sun className="w-5 h-5" />
            </span>
            HomeSchool Hub
          </Link>
          <Link to="/register" data-testid="blog-cta-register" className="text-sm text-amber-700 hover:text-amber-900 font-medium">
            Start free trial <ArrowRight className="w-3.5 h-3.5 inline-block ml-1" />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-16">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">The HomeSchool Hub Blog</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight">
          Practical guides for Florida homeschool families.
        </h1>
        <p className="mt-4 text-stone-600 max-w-2xl text-lg leading-relaxed">
          Short, actionable reads on FL statute compliance, standardized assessments, and every scholarship worth applying for.
        </p>

        {articles === null ? (
          <div className="mt-12 text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading articles…</div>
        ) : articles.length === 0 ? (
          <div className="mt-12 text-stone-500">No articles yet — check back soon.</div>
        ) : (
          <div className="mt-12 grid gap-8 md:grid-cols-2" data-testid="blog-grid">
            {articles.map((a) => (
              <article key={a.slug} className="bg-white border border-stone-200 rounded-3xl overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                <Link to={`/blog/${a.slug}`} data-testid={`blog-card-${a.slug}`}>
                  {a.hero && <img src={a.hero} alt={a.title} className="w-full h-48 object-cover" loading="lazy" />}
                  <div className="p-6">
                    <div className="flex items-center gap-2 flex-wrap">
                      {(a.tags || []).map((t) => (
                        <span key={t} className="rounded-full bg-amber-50 text-amber-800 text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 border border-amber-100">{t}</span>
                      ))}
                    </div>
                    <h2 className="mt-4 font-display text-xl font-semibold leading-snug">{a.title}</h2>
                    <p className="mt-3 text-stone-600 text-sm leading-relaxed line-clamp-3">{a.description}</p>
                    <div className="mt-5 flex items-center gap-4 text-xs text-stone-500">
                      {a.date && <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{new Date(a.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>}
                      <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{a.read_mins} min read</span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-stone-200 py-8 text-center text-sm text-stone-500">
        <div className="mx-auto max-w-5xl px-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
          <span>© {new Date().getFullYear()} HomeSchool Hub — Made for Florida homeschool families.</span>
          <FeedbackLink variant="muted" label="Send feedback" />
        </div>
      </footer>
    </div>
  );
}
